import { useEffect, useMemo, useState } from "react";
import { Button, DatePicker, Input, Modal, Select, Spin } from "antd";
import {
  fetchBriefBankMatterFolders,
  searchBriefBankMatters,
  uploadBriefBankEntry,
} from "../../service/briefBank";
import {
  FilterLabel,
  MatterResult,
  MatterResults,
  Warning,
} from "./BriefBankStyled";

const BRIEF_TYPES = [
  { value: "summary_of_issues", label: "Summary of Issues" },
  { value: "appellants_brief", label: "Appellant's Brief" },
  { value: "appellants_reply", label: "Appellant's Reply" },
  { value: "joint_motion_for_remand", label: "Joint Motion for Remand (JMR)" },
  {
    value: "joint_motion_for_partial_remand",
    label: "Joint Motion for Partial Remand (JMPR)",
  },
];

const matterLabel = (matter) =>
  matter?.display_name ||
  matter?.name ||
  matter?.client_name ||
  matter?.search_client_name ||
  `Matter ${matter?.id}`;

const normalizeList = (response, keys = []) => {
  const payload = response?.data?.data || response?.data || response || {};
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  return Array.isArray(payload?.results) ? payload.results : [];
};

const apiError = (error, fallback) =>
  error?.response?.data?.error ||
  error?.response?.data?.detail ||
  error?.message ||
  fallback;

export default function BriefBankUploadModal({
  open,
  facets,
  onClose,
  onUploaded,
}) {
  const [wordFile, setWordFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [practiceArea, setPracticeArea] = useState("");
  const [briefType, setBriefType] = useState("");
  const [filedOn, setFiledOn] = useState(null);
  const [matterQuery, setMatterQuery] = useState("");
  const [matterResults, setMatterResults] = useState([]);
  const [selectedMatter, setSelectedMatter] = useState(null);
  const [folders, setFolders] = useState([]);
  const [folderId, setFolderId] = useState("");
  const [searching, setSearching] = useState(false);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const availablePracticeAreas =
    facets?.practice_areas?.length > 0
      ? facets.practice_areas
      : [{ practice_area: "va_cavc", count: 0 }];

  useEffect(() => {
    const areas =
      facets?.practice_areas?.length > 0
        ? facets.practice_areas
        : [{ practice_area: "va_cavc" }];
    setWordFile(null);
    setPdfFile(null);
    setPracticeArea(open && areas.length === 1 ? areas[0].practice_area : "");
    setBriefType("");
    setFiledOn(null);
    setMatterQuery("");
    setMatterResults([]);
    setSelectedMatter(null);
    setFolders([]);
    setFolderId("");
    setSearching(false);
    setLoadingFolders(false);
    setUploading(false);
    setError("");
  }, [facets?.practice_areas, open]);

  useEffect(() => {
    if (!open || selectedMatter || matterQuery.trim().length < 2) {
      setMatterResults([]);
      setSearching(false);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    const timeout = setTimeout(async () => {
      try {
        const response = await searchBriefBankMatters(matterQuery.trim());
        if (!cancelled) {
          setMatterResults(normalizeList(response, ["matters"]));
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(apiError(requestError, "Matter search failed."));
          setMatterResults([]);
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [matterQuery, open, selectedMatter]);

  useEffect(() => {
    if (!selectedMatter?.id) {
      setFolders([]);
      setFolderId("");
      return undefined;
    }
    let cancelled = false;
    setFolders([]);
    setFolderId("");
    setLoadingFolders(true);
    fetchBriefBankMatterFolders(selectedMatter.id)
      .then((response) => {
        if (!cancelled) setFolders(normalizeList(response, ["folders"]));
      })
      .catch((requestError) => {
        if (!cancelled) {
          setError(apiError(requestError, "Could not load Matter folders."));
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingFolders(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedMatter]);

  const selectedMatterLabel = useMemo(
    () => (selectedMatter ? matterLabel(selectedMatter) : ""),
    [selectedMatter]
  );

  const practiceOptions = availablePracticeAreas.map((item) => ({
    value: item.practice_area,
    label: item.practice_area === "va_cavc" ? "VA CAVC" : item.practice_area,
  }));

  const submit = async () => {
    if (!wordFile || !practiceArea || !briefType || uploading) return;
    setUploading(true);
    setError("");
    const formData = new FormData();
    formData.append("word_file", wordFile);
    if (pdfFile) formData.append("pdf_file", pdfFile);
    formData.append("practice_area", practiceArea);
    formData.append("brief_type", briefType);
    if (filedOn) formData.append("filed_on", filedOn.format("YYYY-MM-DD"));
    if (selectedMatter?.id) formData.append("matter_id", selectedMatter.id);
    if (folderId) formData.append("folder_id", folderId);

    try {
      const response = await uploadBriefBankEntry(formData);
      onUploaded(response?.data || response);
    } catch (requestError) {
      setError(apiError(requestError, "The brief could not be uploaded."));
      setUploading(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Upload brief"
      onCancel={uploading ? undefined : onClose}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={uploading}>
          Cancel
        </Button>,
        <Button
          key="upload"
          type="primary"
          loading={uploading}
          disabled={uploading || !wordFile || !practiceArea || !briefType}
          onClick={submit}
        >
          Upload and analyze
        </Button>,
      ]}
      width={620}
      destroyOnHidden
    >
      <Warning>
        Upload the Word template. An optional same-named PDF marks it Final.
        Source Matter is optional.
      </Warning>
      {error && <div role="alert" style={{ color: "#b42318", marginBottom: 12 }}>{error}</div>}
      <div style={{ display: "grid", gap: 14 }}>
        <FilterLabel>
          Word brief
          <input
            aria-label="Word brief file"
            type="file"
            accept=".doc,.docx"
            onChange={(event) => {
              setWordFile(event.target.files?.[0] || null);
            }}
          />
          {wordFile?.name && <span>{wordFile.name}</span>}
        </FilterLabel>
        <FilterLabel>
          Matching final PDF (optional)
          <input
            aria-label="Final PDF file"
            type="file"
            accept=".pdf"
            onChange={(event) => {
              setPdfFile(event.target.files?.[0] || null);
            }}
          />
          {pdfFile?.name && <span>{pdfFile.name}</span>}
        </FilterLabel>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <FilterLabel>
            Practice area
            <Select
              value={practiceArea || undefined}
              placeholder="Choose practice area"
              options={practiceOptions}
              onChange={setPracticeArea}
            />
          </FilterLabel>
          <FilterLabel>
            Brief type
            <Select
              value={briefType || undefined}
              placeholder="Choose brief type"
              options={BRIEF_TYPES}
              onChange={setBriefType}
            />
          </FilterLabel>
        </div>
        <FilterLabel>
          Filing date (optional)
          <DatePicker value={filedOn} onChange={setFiledOn} />
        </FilterLabel>
        <FilterLabel>
          Source Matter (optional)
          <Input
            value={selectedMatter ? selectedMatterLabel : matterQuery}
            placeholder="Search by client or Matter"
            onChange={(event) => {
              setSelectedMatter(null);
              setMatterQuery(event.target.value);
            }}
            suffix={searching ? <Spin size="small" /> : null}
            disabled={uploading}
          />
        </FilterLabel>
        {!selectedMatter && matterResults.length > 0 && (
          <MatterResults>
            {matterResults.map((matter) => (
              <MatterResult
                key={matter.id}
                type="button"
                onClick={() => {
                  setSelectedMatter(matter);
                  setMatterQuery("");
                  setMatterResults([]);
                }}
              >
                {matterLabel(matter)}
              </MatterResult>
            ))}
          </MatterResults>
        )}
        {selectedMatter && (
          <Button
            type="link"
            style={{ width: "fit-content", padding: 0 }}
            onClick={() => {
              setSelectedMatter(null);
              setMatterQuery("");
            }}
          >
            Clear source Matter
          </Button>
        )}
        {selectedMatter && (
          <FilterLabel>
            Source folder (optional)
            <Select
              allowClear
              loading={loadingFolders}
              value={folderId || undefined}
              placeholder="Matter root"
              options={folders.map((folder) => ({
                value: folder.id || folder.unique_id,
                label: folder.name,
              }))}
              onChange={(value) => setFolderId(value || "")}
            />
          </FilterLabel>
        )}
      </div>
    </Modal>
  );
}
